/*
  Product page (sections/main-product.liquid): gallery, variants, quantity, the collapsed story and the sticky bar
  on phones.
  - Gallery: scroll-snap stage with a counter, arrows, thumbnails; YouTube loads only when pressed.
  - Variants: option buttons pick the variant, update the URL, price, availability, button and image.
  - Sticky bar: shows once the main buy button has scrolled out of view.
*/
const root = document.querySelector('[data-product-root]');
const money = (cents) => (window.SHF?.money ? window.SHF.money(cents) : `$${(cents / 100).toFixed(2)}`);

/* ------------------------------------------------------------ gallery */
const gallery = root?.querySelector('[data-gallery]');
const track = gallery?.querySelector('[data-gallery-track]');
const slides = track ? [...track.children] : [];
const thumbs = gallery ? [...gallery.querySelectorAll('[data-gallery-thumb]')] : [];
const indexLabel = gallery?.querySelector('[data-gallery-index]');

function goTo(i, smooth = true) {
  if (!track || !slides[i]) return;
  track.scrollTo({ left: slides[i].offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
}
function current() {
  return track ? Math.round(track.scrollLeft / track.clientWidth) : 0;
}
if (track) {
  const start = Number(gallery.dataset.start || 0);
  if (start) requestAnimationFrame(() => goTo(start, false));
  let raf;
  track.addEventListener(
    'scroll',
    () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const i = current();
        if (indexLabel) indexLabel.textContent = String(i + 1);
        thumbs.forEach((t, n) => t.setAttribute('aria-current', String(n === i)));
        slides.forEach((s, n) => n !== i && s.querySelector('video')?.pause());
      });
    },
    { passive: true },
  );
  thumbs.forEach((t) => t.addEventListener('click', () => goTo(Number(t.dataset.galleryThumb))));
  gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => goTo(Math.max(0, current() - 1)));
  gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => goTo(Math.min(slides.length - 1, current() + 1)));
  track.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') goTo(Math.min(slides.length - 1, current() + 1));
    if (e.key === 'ArrowLeft') goTo(Math.max(0, current() - 1));
  });
  track.tabIndex = 0;
}

gallery?.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-yt]');
  if (!btn) return;
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.youtube-nocookie.com/embed/${btn.dataset.yt}?autoplay=1&rel=0`;
  iframe.title = btn.getAttribute('aria-label') || 'Product video';
  iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
  iframe.className = 'absolute inset-0 size-full';
  btn.replaceWith(iframe);
});

/* ------------------------------------------------------------ quantity */
root?.querySelectorAll('[data-qty-step]').forEach((btn) => {
  btn.addEventListener('click', () => {
    const input = btn.closest('[data-qty]').querySelector('input');
    input.value = Math.max(1, (Number(input.value) || 1) + Number(btn.dataset.qtyStep));
  });
});

/* ------------------------------------------------------------ variants */
const variantsJson = root?.querySelector('[data-variants-json]');
if (variantsJson) {
  const variants = JSON.parse(variantsJson.textContent);
  const picker = root.querySelector('[data-variant-picker]');
  const idInput = root.querySelector('[data-variant-id]');
  const addButton = root.querySelector('[data-add-button]');
  const stickyAdd = document.querySelector('[data-sticky-add]');
  const stickyPrice = document.querySelector('[data-sticky-price]');
  const express = root.querySelector('[data-express-checkout]');
  const soldOutEnquiry = root.querySelector('[data-sold-out-enquiry]');
  const stockText = root.querySelector('[data-stock-text]');
  const priceBox = root.querySelector('[data-product-price]');
  const hidePrice = root.dataset.hidePrice === 'true';
  const mode = root.dataset.mode;

  picker.addEventListener('change', () => {
    const chosen = [...picker.querySelectorAll('fieldset')].map((fs) => fs.querySelector('input:checked')?.value);
    picker.querySelectorAll('[data-option-selected]').forEach((el) => (el.textContent = chosen[Number(el.dataset.optionSelected)]));
    const v = variants.find((x) => x.options.every((o, i) => o === chosen[i]));
    if (!v) {
      if (addButton) {
        addButton.disabled = true;
        addButton.textContent = 'Unavailable';
      }
      if (stickyAdd) stickyAdd.disabled = true;
      if (express) express.hidden = true;
      return;
    }
    idInput.value = v.id;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', v.id);
    window.history.replaceState({}, '', url);

    if (addButton) {
      addButton.disabled = !v.available;
      addButton.textContent = v.available ? addButton.dataset.labelAvailable : addButton.dataset.labelUnavailable;
    }
    if (stickyAdd) stickyAdd.disabled = !v.available;
    if (express) express.hidden = !v.available;
    soldOutEnquiry?.classList.toggle('hidden', v.available);

    if (!hidePrice && priceBox) {
      const onSale = v.compare > v.price;
      priceBox.innerHTML = `<div class="price flex flex-col gap-0.5"><p class="flex flex-wrap items-baseline gap-x-2.5"><span class="tabular text-2xl tracking-[-0.01em] font-medium ${onSale ? 'text-signal-ink' : 'text-ink'}">${money(v.price)}</span>${onSale ? `<s class="tabular text-sm text-muted">${money(v.compare)}</s>` : ''}</p>${onSale ? `<p class="text-xs font-semibold tracking-[0.04em] text-signal-ink">Save ${money(v.compare - v.price)}</p>` : ''}</div>`;
      if (stickyPrice) stickyPrice.textContent = money(v.price);
    }
    if (stockText && mode === 'buy') {
      const tone = !v.available ? 'out' : v.stocked ? 'in' : 'low';
      const label = stockText.querySelector('span:last-child');
      const dot = stockText.querySelector('span:first-child');
      if (label) label.textContent = { in: 'In stock and ready to ship', low: 'Available to order: we confirm delivery timing when you order', out: 'Sold out online: ask us about availability' }[tone];
      stockText.classList.remove('font-medium', 'text-stock-in', 'text-ink/75', 'text-stock-out');
      stockText.classList.add(...{ in: ['font-medium', 'text-stock-in'], low: ['text-ink/75'], out: ['text-stock-out'] }[tone]);
      dot?.classList.remove('bg-stock-in', 'bg-smoke', 'bg-stock-out');
      dot?.classList.add({ in: 'bg-stock-in', low: 'bg-smoke', out: 'bg-stock-out' }[tone]);
    }
    if (v.image) {
      const i = slides.findIndex((s) => Number(s.dataset.mediaId) === v.image);
      if (i >= 0) goTo(i);
    }
  });
}

/* ------------------------------------------------------------ story (collapsed after a screenful) */
const story = root?.querySelector('[data-story]');
const storyToggle = root?.querySelector('[data-story-toggle]');
if (story && storyToggle) {
  const fade = story.querySelector('[data-story-fade]');
  const fits = story.scrollHeight <= story.clientHeight + 40;
  if (fits) {
    story.setAttribute('data-open', '');
    fade?.remove();
  } else {
    storyToggle.hidden = false;
    storyToggle.addEventListener('click', () => {
      const open = !story.hasAttribute('data-open');
      story.toggleAttribute('data-open', open);
      if (fade) fade.hidden = open;
      storyToggle.setAttribute('aria-expanded', String(open));
      storyToggle.textContent = open ? 'Show less' : 'Read the full story';
      if (!open) story.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }
}

/* ------------------------------------------------------------ specifications (show all) */
const specsToggle = document.querySelector('[data-specs-toggle]');
if (specsToggle) {
  const more = document.querySelectorAll('[data-spec-more]');
  const label = specsToggle.querySelector('[data-specs-label]');
  specsToggle.addEventListener('click', () => {
    const open = specsToggle.getAttribute('aria-expanded') !== 'true';
    more.forEach(el => { el.hidden = false; el.classList.toggle('hidden', !open); });
    specsToggle.setAttribute('aria-expanded', String(open));
    label.textContent = open ? 'Show fewer' : 'Show all specifications';
    if (!open) document.getElementById('ProductSpecs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

/* ------------------------------------------------------------ sticky bar (phones) */
const sticky = document.querySelector('[data-sticky-buy]');
const anchor = root?.querySelector('[data-add-button], [data-product-form] .btn');
if (sticky && anchor && 'IntersectionObserver' in window) {
  new IntersectionObserver(([entry]) => {
    const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
    sticky.toggleAttribute('data-show', show);
    sticky.setAttribute('aria-hidden', String(!show));
    sticky.querySelectorAll('a, button').forEach((el) => (el.tabIndex = show ? 0 : -1));
  }).observe(anchor);
}
